import React from "react";
import { Modal, Tooltip, Button, message } from "antd";
import { workflowTwoById, workflowTwoErrorById } from "../../../../libs/apis";
import { WorkflowTwoI } from "../../../../libs/interfaces";
import { Box, Grid } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
} from "@ant-design/icons";
import { OneCard, OneVideo } from "../../../../libs/shared";
interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
  type?: "video-validation" | "video-charging" | "video-container";
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type:
    | "external"
    | "internal"
    | "exit"
    | "naviera"
    | "exporter"
    | "other"
    | "gps"
    | "video"
    | "validation"
    | "charging"
    | "container"
    | "video-charging";
}

interface ShowContainerPropsI {
  id: number;
  isError?: boolean;
}

export const ShowContainer = ({ id, isError }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };
  
  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer
          open={open}
          onClose={handleClose}
          id={id}
          isError={isError}
        />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
  isError?: boolean;
}

export const ModalContainer = ({
  open,
  onClose,
  id,
  isError,
}: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<WorkflowTwoI>({
    container: "",
  } as WorkflowTwoI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = React.useState<OnePhotoI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as WorkflowTwoI);
      setLoading(true);
    };
  }, []);

  const handleGetData = async () => {
    try {
      let dataContainer: WorkflowTwoI;
      if (isError) {
        const { data: dataContainerError } = await workflowTwoErrorById(id);
        if (!dataContainerError) return;
        dataContainer = dataContainerError;
        setData(dataContainer);
      } else {
        const { data: dataContainerGet } = await workflowTwoById(id);
        if (!dataContainerGet) return;
        dataContainer = dataContainerGet;
        setData(dataContainerGet);
      }

      const lasFotos: string[] = [];
      const arrCards: any[] = [];

      // Fotos del vacío externo
      const panoramicPhotos = [
        {
          title: "Proceso 1",
          src: dataContainer.emptyPanoramicCheckPhoto,
          alt: "Proceso 1",
          type: "validation",
        },
        {
          title: "Validación",
          src: dataContainer.emptyPanoramicValidationPhoto,
          alt: "Validación",
          comment: dataContainer.emptyPanoramicCheckComment,
          type: "validation",
        },
      ];

      arrCards.push({
        title: "Foto Panorámica",
        noLine: true,
        photos: panoramicPhotos.map((photo) => ({
          src: photo.src,
          alt: photo.title,
          showAlt: true,
        })),
        value: panoramicPhotos[1]?.comment ?? "",
        type: "validation",
      });

      panoramicPhotos.forEach((photo) => {
        lasFotos.push(photo.src);
      });

      const stampNavieraPhotos = [
        {
          title: "Proceso 1",
          src: dataContainer.emptyStampNavieraCheckPhoto,
          alt: "Proceso 1",
          type: "validation",
        },
        {
          title: "Validación",
          src: dataContainer.emptyStampNavieraValidationPhoto,
          alt: "Validación",
          comment: dataContainer.emptyStampNavieraCheckComment,
          type: "validation",
        },
      ];

      arrCards.push({
        title: "Foto Sello Naviera",
        noLine: true,
        photos: stampNavieraPhotos.map((photo) => ({
          src: photo.src,
          alt: photo.title,
          showAlt: true,
        })),
        value: stampNavieraPhotos[1]?.comment ?? "",
        type: "validation",
      });

      stampNavieraPhotos.forEach((photo) => {
        lasFotos.push(photo.src);
      });

      const otherStampPhotos = [
        {
          title: "Proceso 1",
          src: dataContainer.emptyOtherStampCheckPhoto,
          alt: "Proceso 1",
          type: "validation",
        },
        {
          title: "Validación",
          src: dataContainer.emptyOtherStampValidationPhoto,
          alt: "Validación",
          comment: dataContainer.emptyOtherStampCheckComment,
          type: "validation",
        },
      ];

      arrCards.push({
        title: "Foto Otro Sello",
        noLine: true,
        photos: otherStampPhotos.map((photo) => ({
          src: photo.src,
          alt: photo.title,
          showAlt: true,
        })),
        value: otherStampPhotos[1]?.comment ?? "",
        type: "validation",
      });

      otherStampPhotos.forEach((photo) => {
        lasFotos.push(photo.src);
      });

      const satelliteLockPhotos = [
        {
          title: "Proceso 1",
          src: dataContainer.emptySatelliteLockCheckPhoto,
          alt: "Proceso 1",
          type: "validation",
        },
        {
          title: "Validación",
          src: dataContainer.emptySatelliteLockValidationPhoto,
          alt: "Validación",
          comment: dataContainer.emptySatelliteLockCheckComment,
          type: "validation",
        },
      ];

      arrCards.push({
        title: "Foto Sello Candado Satelital",
        noLine: true,
        photos: satelliteLockPhotos.map((photo) => ({
          src: photo.src,
          alt: photo.title,
          showAlt: true,
        })),
        value: satelliteLockPhotos[1]?.comment ?? "",
        type: "validation",
      });

      satelliteLockPhotos.forEach((photo) => {
        lasFotos.push(photo.src);
      });

      const engineryPhotos1 = [
        {
          title: "Proceso 1",
          src: dataContainer.exitEngineryCheckPhoto1,
          alt: "Proceso 1",
          type: "validation",
        },
        {
          title: "Validación",
          src: dataContainer.exitEngineryValidationPhoto1,
          alt: "Validación",
          comment: dataContainer.exitEngineryCheckComment1,
          type: "validation",
        },
      ];

      arrCards.push({
        title: "Foto Maquinaria 1",
        noLine: true,
        photos: engineryPhotos1.map((photo) => ({
          src: photo.src,
          alt: photo.title,
          showAlt: true,
        })),
        value: engineryPhotos1[1]?.comment ?? "",
        type: "validation",
      });

      engineryPhotos1.forEach((photo) => {
        lasFotos.push(photo.src);
      });

      const engineryPhotos2 = [
        {
          title: "Proceso 2",
          src: dataContainer.exitEngineryCheckPhoto2,
          alt: "Proceso 2",
          type: "validation",
        },
        {
          title: "Validación",
          src: dataContainer.exitEngineryValidationPhoto2,
          alt: "Validación",
          comment: dataContainer.exitEngineryCheckComment2,
          type: "validation",
        },
      ];

      arrCards.push({
        title: "Foto Maquinaria 2",
        noLine: true,
        photos: engineryPhotos2.map((photo) => ({
          src: photo.src,
          alt: photo.title,
          showAlt: true,
        })),
        value: engineryPhotos2[1]?.comment ?? "",
        type: "validation",
      });

      engineryPhotos2.forEach((photo) => {
        lasFotos.push(photo.src);
      });

      const videos = [
        {
          src: dataContainer.exitDoorCheckVideo,
          alt: "Video Proceso 1 Puerta",
          type: "video-validation",
        },
        {
          src: dataContainer.exitDoorValidationVideo,
          alt: "Video Validación Puerta",
          type: "video-validation",
        },
        {
          src: dataContainer.exitEngineryCheckVideo,
          alt: "Video Proceso 1 Maquinaria",
          type: "video-validation",
        },
        {
          src: dataContainer.exitEngineryValidationVideo,
          alt: "Video Validación Maquinaria",
          type: "video-validation",
        },
      ] as OnePhotoI[];

      const chargingProcessPhotos = [
        {
          title: "Foto 1",
          src: dataContainer.chargingProcessPhoto1,
          alt: "Proceo de carga 1",
          comment: dataContainer.chargingProcessComment1,
          type: "charging",
        },
        {
          title: "Foto 2",
          src: dataContainer.chargingProcessPhoto2,
          alt: "Proceo de carga 1",
          comment: dataContainer.chargingProcessComment2,
          type: "charging",
        },
      ];

      chargingProcessPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "charging",
        });
        lasFotos.push(photo.src);
      });

      videos.push({
        src: dataContainer.chargingProcessVideo,
        alt: "Video Proceso de Carga",
        type: "video-charging",
      });

      const containerPhotos = [
        {
          title: "Foto 1",
          src: dataContainer.containerPanoramicPhoto,
          alt: "Contenedor 1",
          comment: dataContainer.containerPanoramicComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Botella Naviera",
          src: dataContainer.navieraBottlePhoto,
          alt: "Contenedor",
          comment: dataContainer.navieraBottleComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Cable Naviera",
          src: dataContainer.navieraWirePhoto,
          alt: "Contenedor",
          comment: dataContainer.navieraWireComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Etiqueta Naviera",
          src: dataContainer.navieraLabelPhoto,
          alt: "Contenedor",
          comment: dataContainer.navieraLabelComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Botella Exportador",
          src: dataContainer.exporterBottlePhoto,
          alt: "Contenedor 3",
          comment: dataContainer.exporterBottleComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Cable Exportador",
          src: dataContainer.exporterWirePhoto,
          alt: "Contenedor",
          comment: dataContainer.exporterWireComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Etiqueta Exportador",
          src: dataContainer.exporterLabelPhoto,
          alt: "Contenedor",
          comment: dataContainer.exporterLabelComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Botella Otro",
          src: dataContainer.otherBottlePhoto,
          alt: "Contenedor 4",
          comment: dataContainer.otherBottleComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Cable Otro",
          src: dataContainer.otherWirePhoto,
          alt: "Contenedor",
          comment: dataContainer.otherWireComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Etiqueta Otro",
          src: dataContainer.otherLabelPhoto,
          alt: "Contenedor",
          comment: dataContainer.otherLabelComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Gps",
          src: dataContainer.gpsPhoto,
          alt: "Contenedor",
          comment: dataContainer.gpsComment,
          type: "container",
          showAlt: true,
        },
        {
          title: "Foto Sello Gps",
          src: dataContainer.gpsStampPhoto,
          alt: "Contenedor",
          comment: dataContainer.gpsStampComment,
          type: "container",
          showAlt: true,
        },
      ];

      containerPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "container",
        });
        lasFotos.push(photo.src);
      });

      videos.push({
        src: dataContainer.exitDoorVideo,
        alt: "Video Puerta",
        type: "video-container",
      });

      videos.push({
        src: dataContainer.exitEngineryVideo,
        alt: "Video Maquinaria",
        type: "video-container",
      });

      setTotalPhotos(lasFotos);
      setCards(arrCards);
      setCardVideos(videos);
      setLoading(false);
    } catch (err: any) {
      message.error((err as Error).message);
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <p
          style={{
            color: "white",
            padding: "10px",
            fontWeight: "bold",
            fontSize: "1.2rem",
            textAlign: "center",
            background: "linear-gradient(to bottom, #FFA500, #FF4500)", // Naranja a rojo
            borderRadius: "5px",
            width: "98%",
            margin: "0 auto",
          }}
        >
          {`CONTENEDOR N° ${data.container}`}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{
            background: "red",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={810}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      style={{ padding: 0 }}
    >
      <Box sx={{ overflowY: "auto", maxHeight: 550, p: 1 }}>
        {/* Validación */}

        <PhotoSection
          title="Validación"
          type="validation"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Validación"
          type="validation"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Proceso de Carga"
          type="charging"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        <PhotoSection
          title="Fotos Contenedor Full"
          type="container"
          cards={cards}
          totalPhotos={totalPhotos}
        />
        {/* <VideoSection videos={cardVideos} /> */}
      </Box>
    </Modal>
  );
};

const PhotoSection = ({
  title,
  type,
  cards,
  totalPhotos,
}: {
  title: string;
  type: string;
  cards: OneCardI[];
  totalPhotos: string[];
}) => (
  <>
    <Box
      sx={{
        textAlign: "center",
        mb: 2,
        mt: 4,
        bgcolor: "transparent",
        borderRadius: "4px",
        background:
          "linear-gradient(to bottom, #A9A9A9 0%,#14301A 50%,rgb(29, 68, 152) 100%)",
      }}
    >
      <h3 style={{ color: "white" }}>{title}</h3>
    </Box>
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      }}
    >
      {cards
        .filter((el) => el.type === type)
        .map((el) => (
          <OneCard data={el} key={el.title} lasFotos={totalPhotos} />
        ))}
    </Box>
  </>
);

const VideoSection = ({ videos }: { videos: OnePhotoI[] }) => (
  <>
    <Box
      sx={{
        textAlign: "center",
        mb: 4,
        mt: 8,
        bgcolor: "transparent",
        borderRadius: "4px",
        background:
          "linear-gradient(to bottom, #A9A9A9 0%,#14301A 50%,rgb(29, 68, 152) 100%)",
      }}
    >
      <h3 style={{ color: "white" }}>Videos</h3>
    </Box>
    <Grid container spacing={2} justifyContent={"center"}>
      {videos.map((video, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <OneVideo src={video.src!} alt={video.alt} />
        </Grid>
      ))}
    </Grid>
  </>
);
